import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../shared/Layout';
import PageHeader from '../../shared/PageHeader';
import IconGoBack from '../../../components/icons/IconGoBack';
import { ExerciseLinkSection } from './WorkspaceTopicSection';
import { fetchExerciseContent } from './useStudentWorkspaceData';
import { getExerciseTemplate } from '../../exercise-templates/api';
import '../../../css/course-preview.css';
import '../../../css/topic-canvas.css';

// Read-only view of one homework exercise and what the student answered in it.
// The dashboard's "Останні виконані домашні завдання" opens this for homework
// that isn't attached to any course, so there is no course workspace to land
// in. The answers are the same saved-state rows the course workspace reads,
// rendered by the same component, so the result looks identical to the course.
export default function HomeworkPreview() {
   const { studentId, exerciseId } = useParams();
   const navigate = useNavigate();
   const [status, setStatus] = useState('loading');
   const [student, setStudent] = useState(null);
   const [exercise, setExercise] = useState(null);
   const [savedStateByElementId, setSavedStateByElementId] = useState({});
   const [workspaceId, setWorkspaceId] = useState(null);

   useEffect(() => {
      let cancelled = false;
      Promise.all([
         getExerciseTemplate(exerciseId),
         fetchExerciseContent(exerciseId),
         axios.get('/api/students/' + studentId, { withCredentials: true }),
         axios.get('/api/exercisestudentsavedinteractiveelements?student=' + studentId, { withCredentials: true }),
         axios.get('/api/studentcourseworkspaces?student=' + studentId, { withCredentials: true }),
      ]).then(([templateRes, content, studentRes, savedRes, workspacesRes]) => {
         if (cancelled) return;
         // Keep only the states that belong to this exercise's own interactive blocks.
         const interactiveIds = new Set(content.interactiveElements.map(e => e.id));
         const saved = {};
         (Array.isArray(savedRes.data) ? savedRes.data : []).forEach(s => {
            if (s.interactiveElement && interactiveIds.has(s.interactiveElement.id)) {
               saved[s.interactiveElement.id] = s;
            }
         });
         // Notes and markers are stored per workspace; a homework-only workspace
         // has no course. Read-only here, but the id keeps those pins attached.
         const workspaces = Array.isArray(workspacesRes.data) ? workspacesRes.data : [];
         const courseless = workspaces.find(w => !w.course);
         setStudent(studentRes.data);
         setExercise({
            link: { exerciseTemplate: { id: Number(exerciseId), name: templateRes.data.name } },
            elements: content.elements,
            interactiveElements: content.interactiveElements,
            containers: content.containers,
         });
         setSavedStateByElementId(saved);
         setWorkspaceId(courseless ? courseless.id : null);
         setStatus('ready');
      }).catch(err => {
         if (cancelled) return;
         if (err.response && err.response.status === 401) navigate('/login');
         setStatus('error');
      });
      return () => { cancelled = true; };
   }, [studentId, exerciseId, navigate]);

   const studentName = student ? ((student.name || '') + ' ' + (student.surname || '')).trim() : '';

   return (
      <Layout>
         <PageHeader name={
            <div className='course-workspace-header-inner'>
               <div className='course-workspace-back-btn' onClick={() => navigate('/students/' + studentId)} title='До картки студента'>
                  <IconGoBack />
               </div>
               <span className='course-workspace-title'>
                  {exercise ? exercise.link.exerciseTemplate.name : 'Домашнє завдання'}
               </span>
            </div>
         }>
            {studentName ? <div className='homework-preview-sub'>Домашнє завдання · {studentName}</div> : ''}
         </PageHeader>
         <main className='main-block'>
            <div className='main-content homework-preview-page'>
               {status === 'loading' ? <p className='course-preview-empty-hint'>Завантаження...</p> : ''}
               {status === 'error' ? <p className='course-preview-empty-hint'>Не вдалося завантажити вправу.</p> : ''}
               {status === 'ready' ? (
                  <ExerciseLinkSection exercise={exercise} notesByTarget={{}} progressByElementId={{}}
                     savedStateByElementId={savedStateByElementId} workspaceId={workspaceId}
                     studentId={studentId} onNotesChanged={() => {}} homeworkByExerciseTemplateId={{}} />
               ) : ''}
            </div>
         </main>
      </Layout>
   );
}
